import React, { useMemo, useState } from 'react';
import { MeetingMember, meetingService } from '../../services/meetingService';
import {
  collectMemberEmails,
  formatInterestDate,
  formatInterestResponsesDetail,
} from '../../data/workshop2026InterestSurvey';
import { formatNameFirstLastInitial } from '../../utils/userState';
import styles from '../../styles/meetingSettingsPage.module.css';

interface WorkshopParticipantsAdminProps {
  meetingCode: string;
  members: MeetingMember[];
  canEdit: boolean;
  onReload: () => Promise<void>;
  onMessage: (msg: string) => void;
  onError: (msg: string) => void;
}

function registrationLabel(state: MeetingMember['registration_state']): string {
  if (state === 'confirmed') return 'Confirmed';
  if (state === 'interest') return 'Interest';
  if (state === 'none' || !state) return 'Member';
  return state;
}

function signedUpDate(member: MeetingMember): string {
  if (member.registration_state === 'confirmed' && member.confirmed_at) {
    return formatInterestDate(member.confirmed_at) || '—';
  }
  if (member.registration_state === 'interest' && member.interest_at) {
    return formatInterestDate(member.interest_at) || '—';
  }
  if (member.joined_at) {
    return formatInterestDate(member.joined_at) || '—';
  }
  return '—';
}

function phoneListLabel(member: MeetingMember): string {
  if (!member.include_on_printed_phone_list) return 'Not on phone list';
  if (member.sponsor_willingness === 'yes') return 'On phone list · Sponsor';
  if (member.sponsor_willingness === 'temporary') return 'On phone list · Temp sponsor';
  return 'On phone list';
}

const WorkshopParticipantsAdmin: React.FC<WorkshopParticipantsAdminProps> = ({
  meetingCode,
  members,
  canEdit,
  onReload,
  onMessage,
  onError,
}) => {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [emailPreview, setEmailPreview] = useState<'interest' | 'confirmed' | null>(null);

  const interestCount = members.filter((m) => m.registration_state === 'interest').length;
  const confirmedCount = members.filter((m) => m.registration_state === 'confirmed').length;
  const otherCount = members.filter(
    (m) => m.registration_state !== 'interest' && m.registration_state !== 'confirmed'
  ).length;

  const sortedMembers = useMemo(() => {
    const rank = (m: MeetingMember) => {
      if (m.registration_state === 'confirmed') return 0;
      if (m.registration_state === 'interest') return 1;
      return 2;
    };
    return [...members].sort((a, b) => {
      const r = rank(a) - rank(b);
      if (r !== 0) return r;
      return (a.display_name || '').localeCompare(b.display_name || '', undefined, {
        sensitivity: 'base',
      });
    });
  }, [members]);

  const interestEmails = useMemo(
    () => collectMemberEmails(members, 'interest'),
    [members]
  );
  const confirmedEmails = useMemo(
    () => collectMemberEmails(members, 'confirmed'),
    [members]
  );

  const copyEmails = async (list: 'interest' | 'confirmed') => {
    const emails = list === 'interest' ? interestEmails : confirmedEmails;
    if (emails.length === 0) {
      onError(`No ${list} list emails to copy.`);
      return;
    }
    const text = emails.join(', ');
    try {
      await navigator.clipboard.writeText(text);
      onMessage(
        `Copied ${emails.length} ${list === 'interest' ? 'interest' : 'confirmed'} email${emails.length === 1 ? '' : 's'}. Paste into Gmail To or Bcc.`
      );
      setEmailPreview(list);
    } catch {
      setEmailPreview(list);
      onMessage('Select the email list below and copy manually.');
    }
  };

  const handleAdminConfirm = async (member: MeetingMember) => {
    if (!canEdit || confirmingId) return;
    const name = formatNameFirstLastInitial(member.display_name, member.email || 'this member');
    const ok = window.confirm(
      `Mark ${name} as confirmed for the workshop? They will receive the confirmation email if one is configured.`
    );
    if (!ok) return;
    setConfirmingId(member.user_id);
    try {
      const result = await meetingService.adminConfirmWorkshopMember(meetingCode, member.user_id);
      await onReload();
      onMessage(
        result.confirmation_email_sent
          ? `${name} confirmed. Confirmation email sent.`
          : `${name} confirmed.`
      );
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to confirm member');
    } finally {
      setConfirmingId(null);
    }
  };

  const previewEmails = emailPreview === 'interest' ? interestEmails : confirmedEmails;

  return (
    <section className={styles.settingsSection}>
      <h2 className={styles.sectionTitle}>Participants</h2>
      <p className={styles.fieldHint}>
        {confirmedCount} confirmed · {interestCount} on interest list
        {otherCount > 0 ? ` · ${otherCount} other member${otherCount === 1 ? '' : 's'}` : ''}
        {' '}
        · {members.length} total
      </p>

      {canEdit && (
        <div className={styles.participantExportRow}>
          <button
            type="button"
            className={styles.secondaryBtn}
            onClick={() => copyEmails('interest')}
            disabled={interestEmails.length === 0}
          >
            Copy interest emails ({interestEmails.length})
          </button>
          <button
            type="button"
            className={styles.secondaryBtn}
            onClick={() => copyEmails('confirmed')}
            disabled={confirmedEmails.length === 0}
          >
            Copy confirmed emails ({confirmedEmails.length})
          </button>
        </div>
      )}

      {emailPreview && previewEmails.length > 0 && (
        <div className={styles.emailExportPreview}>
          <label className={styles.label}>
            {emailPreview === 'interest' ? 'Interest list emails' : 'Confirmed list emails'}
          </label>
          <textarea
            className={styles.textarea}
            rows={3}
            readOnly
            value={previewEmails.join(', ')}
            onFocus={(e) => e.target.select()}
          />
          <button
            type="button"
            className={styles.linkishBtn}
            onClick={() => setEmailPreview(null)}
          >
            Hide
          </button>
        </div>
      )}

      {members.length === 0 ? (
        <p className={styles.membersEmpty}>No participants yet.</p>
      ) : (
        <div className={styles.participantTableWrap}>
          <table className={`${styles.participantTable} ${styles.participantTableCompact}`}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Status</th>
                <th>Signed up</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {sortedMembers.map((m) => {
                const detail = formatInterestResponsesDetail(m.interest_responses);
                const expanded = expandedId === m.user_id;
                const displayName = formatNameFirstLastInitial(m.display_name, m.email || 'Member');
                return (
                  <React.Fragment key={m.user_id}>
                    <tr>
                      <td>
                        {displayName}
                        {m.is_admin && <span className={styles.adminBadgeInline}>Admin</span>}
                      </td>
                      <td>
                        <span
                          className={
                            m.registration_state === 'confirmed'
                              ? styles.statusConfirmed
                              : m.registration_state === 'interest'
                                ? styles.statusInterest
                                : styles.statusOther
                          }
                        >
                          {registrationLabel(m.registration_state)}
                        </span>
                      </td>
                      <td>{signedUpDate(m)}</td>
                      <td className={styles.participantActionsCell}>
                        <button
                          type="button"
                          className={styles.linkishBtn}
                          onClick={() => setExpandedId(expanded ? null : m.user_id)}
                        >
                          {expanded ? 'Hide' : 'Details'}
                        </button>
                      </td>
                    </tr>
                    {expanded && (
                      <tr className={styles.participantDetailRow}>
                        <td colSpan={4}>
                          <dl className={styles.questionnaireDetailList}>
                            <div className={styles.questionnaireDetailItem}>
                              <dt>Email</dt>
                              <dd>{m.email || '—'}</dd>
                            </div>
                            <div className={styles.questionnaireDetailItem}>
                              <dt>Phone list</dt>
                              <dd>{phoneListLabel(m)}</dd>
                            </div>
                            {detail.map((row) => (
                              <div key={row.label} className={styles.questionnaireDetailItem}>
                                <dt>{row.label}</dt>
                                <dd>{row.value}</dd>
                              </div>
                            ))}
                            {canEdit && m.registration_state !== 'confirmed' && (
                              <div className={styles.questionnaireDetailItem}>
                                <dt>Actions</dt>
                                <dd>
                                  <button
                                    type="button"
                                    className={styles.linkishBtn}
                                    disabled={confirmingId === m.user_id}
                                    onClick={() => handleAdminConfirm(m)}
                                  >
                                    {confirmingId === m.user_id ? 'Confirming…' : 'Mark confirmed'}
                                  </button>
                                </dd>
                              </div>
                            )}
                          </dl>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

export default WorkshopParticipantsAdmin;
